import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from 'app/Auth'
import { useAddTrayContext } from '../AddTray.context'
import { useTrayStepContext } from '../AddTray.logic'
import useTrayConfiguration from 'views/TrayConfiguration/TrayConfiguration.logic'
import {
  useCreateTrayItemMutation,
  useGetTrayItemByBarcode,
} from 'lib/apollo/hooks'
import { CreateTrayItemInput } from 'common/types'
import { useUser } from 'app/User'
import dayjs from 'lib/dayjs'
import { printSticker } from 'components/organisms/TraySticker/TraySticker'
import { TrayItem } from '../AddTray.types'
import { enqueueSnackbar } from 'notistack'
import { TraySubmissionService } from './TraySubmission/TraySubmissionService'
import { TraySubmissionSummary } from './TraySubmission/TraySubmission.types'
import { useAuthActions } from 'store/auth/auth.store'
import { useNotifications, useSurgeryActions } from 'store/notifications'

const useReviewLogic = () => {
  const navigate = useNavigate()
  const { logout } = useAuth0()
  const { handleLogout: handleLogoutAction } = useAuthActions()
  const { openSurgeryReminderModal } = useSurgeryActions()
  const { prevStep, reset } = useTrayStepContext()
  const { user, isRep } = useUser()
  const { findLocation } = useTrayConfiguration()
  const { createTrayItem, loading: isCreatingTrayItem } =
    useCreateTrayItemMutation()
  const { getTrayItem } = useGetTrayItemByBarcode()
  const {
    trayWeight,
    selectedTrayCategory,
    traySurgery,
    hasSubTrays,
    trays,
    resetAllTrays,
    isFixingRejectedTray,
    updateRejectedTray,
  } = useAddTrayContext()

  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [logoutTimer, setLogoutTimer] = useState<NodeJS.Timeout | null>(null)
  const [hasClickedPrint, setHasClickedPrint] = useState(false)
  const [submissionSummary, setSubmissionSummary] =
    useState<TraySubmissionSummary | null>(null)
  const [showSubmissionResults, setShowSubmissionResults] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [isSubmittingFinalize, setIsSubmittingFinalize] = useState(false)
  const [isSubmittingAddAnother, setIsSubmittingAddAnother] = useState(false)

  const isAssignedToCase = traySurgery

  // Initialize the submission service
  const submissionService = new TraySubmissionService({
    maxRetries: 3,
    retryDelay: 1000,
    verificationDelay: 2000,
  })

  const createTrayItemInput = (tray: TrayItem): CreateTrayItemInput => {
    const currentTimestamp = new Date()
    const userRole = isRep ? 'rep' : 'spd'

    const otherItems = tray.subTrays[0].content.otherItems

    return {
      companyName: tray.trayVendor,
      locationId: tray.selectedLocationId as number,
      description: tray.trayDescription,
      barcode: tray.trayBarcode,
      isConsigned: tray.selectedTrayCategory === 'consigned',
      productDetails: JSON.stringify({
        surgeryId: tray.surgery?._id,
        surgeryDetails: tray.surgery,
        trayCategory: tray.selectedTrayCategory,
        trayImage: tray.parentTrayImage?.src || undefined,
        otherItems,
        inventorySheetFiles: tray.inventorySheetFiles.map((file) => ({
          src: file.src,
          mimeType: file.mimeType,
        })),
        trayWeight,
        trayType: tray.selectedTrayType,
        status:
          tray.selectedTrayCategory === 'facility'
            ? tray.surgery
              ? 'assigned'
              : 'unAssigned'
            : 'droppedOff',
        statusDetails: {
          lastUpdated: dayjs().toISOString(),
          lastUpdatedBy: user?.email,
        },
        selectedSurgeon: tray.selectedSurgeon,
      }),
      quantity: 1,
      subTrays: tray.subTrays.map((subTray) => {
        const trayImageRecord = {
          imageUrl: subTray.trayImg,
          addedBy: {
            email: user?.email || '',
            role: userRole as 'rep' | 'spd' | 'nurse',
          },
          timestamp: currentTimestamp,
        }

        const trayImageHistory = {
          records: [trayImageRecord],
          latest: {
            [userRole]: trayImageRecord,
          },
        }

        return {
          parLevel: subTray.parLevel,
          trayImg: subTray.trayImg,
          trayImageHistory: trayImageHistory,
          trayType: subTray.trayType,
          description: subTray.description,
          content: JSON.stringify(subTray.content),
        }
      }),
    }
  }

  /**
   * Robust submission handler with retry logic and verification
   */
  const handleRobustSubmit = async (
    traysToSubmit: TrayItem[] = trays,
    setLoadingState: (loading: boolean) => void
  ) => {
    try {
      setLoadingState(true)
      const summary = await submissionService.submitMultipleTrays(
        traysToSubmit,
        createTrayItemInput,
        createTrayItem,
        getTrayItem
      )

      setSubmissionSummary(summary)
      setShowSubmissionResults(true)

      // Show summary notification
      const { message, variant } =
        submissionService.formatSubmissionSummary(summary)
      enqueueSnackbar(message, { variant })

      // If all trays succeeded, handle guest user logout
      if (summary.failureCount === 0 && user?.email === 'guest@medgeo.com') {
        setShowLogoutModal(true)
        startLogoutTimer()
      }

      return summary
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in robust submit:', error)
      enqueueSnackbar('An unexpected error occurred during submission', {
        variant: 'error',
      })
      return null
    } finally {
      setLoadingState(false)
    }
  }

  const handleBack = () => {
    prevStep()
  }

  const handleRejectedTray = async () => {
    // Update the rejected tray instead of creating new trays
    const success = await updateRejectedTray()

    if (success) {
      enqueueSnackbar('Tray updated successfully')

      // Reset and navigate back to tray management
      resetAllTrays()
      return true
    } else {
      enqueueSnackbar('Failed to update tray', {
        variant: 'error',
      })
      return false
    }
  }

  const handleDone = async () => {
    if (isFixingRejectedTray) {
      const success = await handleRejectedTray()
      if (!success) {
        return
      }
      resetAllTrays()
      navigate('/tray-management')
    } else {
      const summary = await handleRobustSubmit(trays, setIsSubmittingFinalize)

      // Only navigate away if all trays were successful
      if (summary && summary.failureCount === 0) {
        resetAllTrays()
        navigate('/tray-management')
      }
      // If there were failures, the user stays on the page and can see the results modal
    }
  }

  const handleAddAnother = async () => {
    const summary = await handleRobustSubmit(trays, setIsSubmittingAddAnother)

    // Only reset and continue if all trays were successful
    if (summary && summary.failureCount === 0) {
      resetAllTrays()
      reset()
    }
    // If there were failures, the user stays on the page and can see the results modal
  }

  /**
   * Retry only the failed trays
   */
  const handleRetryFailedTrays = async () => {
    if (!submissionSummary || submissionSummary.failed.length === 0) {
      return
    }

    setIsRetrying(true)

    try {
      // Find the original tray items that failed
      const failedTrayBarcodes = submissionSummary.failed.map((f) => f.barcode)
      const failedTrays = trays.filter((tray) =>
        failedTrayBarcodes.includes(tray.trayBarcode)
      )

      const retryResults = await submissionService.submitMultipleTrays(
        failedTrays,
        createTrayItemInput,
        createTrayItem,
        getTrayItem
      )

      // Merge with previous results
      const newSuccessful = [
        ...submissionSummary.successful,
        ...retryResults.successful,
      ]

      const newSummary: TraySubmissionSummary = {
        successful: newSuccessful,
        failed: retryResults.failed,
        totalTrays: submissionSummary.totalTrays,
        successCount: newSuccessful.length,
        failureCount: retryResults.failed.length,
      }

      setSubmissionSummary(newSummary)

      const { message, variant } =
        submissionService.formatSubmissionSummary(newSummary)
      enqueueSnackbar(message, { variant })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error retrying failed trays:', error)
      enqueueSnackbar('Error occurred while retrying failed trays', {
        variant: 'error',
      })
    } finally {
      setIsRetrying(false)
    }
  }

  /**
   * Navigate to tray management and close results modal
   */
  const handleNavigateToManagement = () => {
    setShowSubmissionResults(false)
    resetAllTrays()
    navigate('/tray-management')
  }

  /**
   * Close submission results modal
   */
  const handleCloseSubmissionResults = () => {
    setShowSubmissionResults(false)

    // If all trays were successful, we can safely reset
    if (submissionSummary && submissionSummary.failureCount === 0) {
      resetAllTrays()
    }
  }

  const handleModalClose = () => {
    if (logoutTimer) {
      clearTimeout(logoutTimer)
    }
    setShowLogoutModal(false)
  }

  const startLogoutTimer = () => {
    const timer = setTimeout(() => {
      handleLogout()
    }, 15000) // 15 seconds
    setLogoutTimer(timer)
  }

  const notifications = useNotifications()

  const handleLogout = async () => {
    if (logoutTimer) {
      clearTimeout(logoutTimer)
    }

    if (isRep || notifications.length === 0) {
      handleLogoutAction(() => {
        logout({ returnTo: `${window.location.origin}/?status=loggedout` })
      })
      return
    }

    const confirmed = await openSurgeryReminderModal('logout')
    if (!confirmed) return

    handleLogoutAction(() => {
      logout({ returnTo: `${window.location.origin}/?status=loggedout` })
    })
  }

  const handlePrintLabel = () => {
    trays.forEach((tray) => {
      printSticker({
        id: tray.trayBarcode,
        vendor: tray.trayVendor,
        description: tray.trayDescription,
        ownership: tray.selectedTrayCategory,
        type: 'implantable',
      })
    })
    setHasClickedPrint(true)
  }

  const allTrays = trays.map((tray) => ({
    ...tray,
    locationName: tray.selectedLocationId
      ? findLocation(tray.selectedLocationId)?.name
      : '',
  }))

  return {
    handleBack,
    handleDone,
    handleAddAnother,
    isCreatingTrayItem,
    showLogoutModal,
    handleModalClose,
    handleLogout,
    handlePrintLabel,
    hasClickedPrint,
    isAssignedToCase,
    hasSubTrays,
    allTrays,
    isFixingRejectedTray,
    selectedTrayCategory,
    // New submission-related exports
    submissionSummary,
    showSubmissionResults,
    handleRetryFailedTrays,
    handleNavigateToManagement,
    handleCloseSubmissionResults,
    isRetrying,
    isSubmittingFinalize,
    isSubmittingAddAnother,
  }
}

export default useReviewLogic
